import { useState, useRef, useEffect } from 'react';
import { X, RotateCcw, Check, Pen } from 'lucide-react';

export default function SignaturePad({
  onSave,
  onCancel,
  signerName = '',
  signerType = 'customer' // 'customer' or 'contractor'
}) {
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasSignature, setHasSignature] = useState(false);
  const [printedName, setPrintedName] = useState(signerName || '');
  const [isLandscape, setIsLandscape] = useState(window.innerWidth > window.innerHeight);

  // Force fullscreen and track orientation
  useEffect(() => {
    // Try to go fullscreen
    const enterFullscreen = async () => {
      try {
        if (containerRef.current && document.fullscreenEnabled) {
          await containerRef.current.requestFullscreen();
        }
      } catch (err) {

      }
    };

    // Try to lock to landscape on mobile
    const lockOrientation = async () => {
      try {
        if (window.screen?.orientation?.lock) {
          await window.screen.orientation.lock('landscape');
        }
      } catch (err) {

      }
    };

    enterFullscreen();
    lockOrientation();

    // Track orientation changes
    const handleResize = () => {
      setIsLandscape(window.innerWidth > window.innerHeight);
    };
    window.addEventListener('resize', handleResize);

    // Cleanup - exit fullscreen and unlock orientation
    return () => {
      window.removeEventListener('resize', handleResize);
      if (document.fullscreenElement) {
        document.exitFullscreen().catch(() => {});
      }
      if (window.screen?.orientation?.unlock) {
        try {
          window.screen.orientation.unlock();
        } catch (err) {}
      }
    };
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');

    // Set canvas size
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * 2; // High DPI
    canvas.height = rect.height * 2;
    ctx.scale(2, 2);

    // Set drawing style
    ctx.strokeStyle = '#1a1a1a';
    ctx.lineWidth = 3;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';

    // Clear canvas with white background
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
  }, [isLandscape]);

  const startDrawing = (e) => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    
    const x = (e.clientX || e.touches?.[0]?.clientX) - rect.left;
    const y = (e.clientY || e.touches?.[0]?.clientY) - rect.top;
    
    ctx.beginPath();
    ctx.moveTo(x, y);
    setIsDrawing(true);
    setHasSignature(true);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    
    e.preventDefault(); // Prevent scrolling on mobile
    
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    
    const x = (e.clientX || e.touches?.[0]?.clientX) - rect.left;
    const y = (e.clientY || e.touches?.[0]?.clientY) - rect.top;
    
    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const stopDrawing = () => {
    setIsDrawing(false);
  };

  const clearSignature = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    setHasSignature(false);
  };

  const saveSignature = () => {
    if (!hasSignature) {
      alert('Please provide a signature');
      return;
    }
    
    if (!printedName.trim()) {
      alert('Please enter your printed name');
      return;
    }

    const canvas = canvasRef.current;
    const signatureData = canvas.toDataURL('image/png');
    
    onSave({
      signatureData,
      printedName: printedName.trim(),
      signerType,
      timestamp: new Date().toISOString(),
      ipAddress: null // Can be captured server-side if needed
    });
  };

  return (
    <div
      ref={containerRef}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: '#fff',
        display: 'flex',
        flexDirection: 'column',
        zIndex: 10000,
        overflow: 'hidden'
      }}
    >
      {/* Header Bar */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '0.75rem 1.5rem',
        borderBottom: '2px solid #F05A28',
        background: '#fafafa'
      }}>
        <h2 style={{
          margin: 0,
          fontSize: isLandscape ? '1.25rem' : '1.1rem',
          color: '#1a1a1a',
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem'
        }}>
          <Pen size={20} color="#F05A28" />
          {signerType === 'customer' ? 'Customer' : 'Contractor'} Signature
        </h2>
        <button
          onClick={onCancel}
          style={{
            background: '#f5f5f5',
            border: 'none',
            borderRadius: '8px',
            padding: '0.5rem 1rem',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            fontSize: '0.9rem',
            color: '#666'
          }}
        >
          <X size={20} />
          Cancel
        </button>
      </div>

      {/* Main Content - Landscape: side by side, Portrait: stacked */}
      <div style={{
        flex: 1,
        display: 'flex',
        flexDirection: isLandscape ? 'row' : 'column',
        overflow: 'hidden'
      }}>
        {/* Signature Canvas Section - Takes more space in landscape */}
        <div style={{
          flex: isLandscape ? 2 : 'none',
          display: 'flex',
          flexDirection: 'column',
          padding: isLandscape ? '1.5rem' : '1rem',
          borderRight: isLandscape ? '1px solid #e0e0e0' : 'none',
          borderBottom: isLandscape ? 'none' : '1px solid #e0e0e0'
        }}>
          <div style={{
            fontSize: '0.8rem',
            color: '#666',
            marginBottom: '0.5rem',
            textTransform: 'uppercase',
            fontWeight: '600',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}>
            <span style={{ color: '#F05A28' }}>Step 1:</span> Sign Here
          </div>
          <div style={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            background: '#f9f9f9',
            border: '3px solid #F05A28',
            borderRadius: '12px',
            padding: '0.75rem',
            minHeight: isLandscape ? 'auto' : '180px'
          }}>
            <canvas
              ref={canvasRef}
              onMouseDown={startDrawing}
              onMouseMove={draw}
              onMouseUp={stopDrawing}
              onMouseLeave={stopDrawing}
              onTouchStart={startDrawing}
              onTouchMove={draw}
              onTouchEnd={stopDrawing}
              style={{
                flex: 1,
                width: '100%',
                background: 'white',
                border: '2px dashed #e0e0e0',
                borderRadius: '8px',
                cursor: 'crosshair',
                touchAction: 'none'
              }}
            />
            <button
              onClick={clearSignature}
              disabled={!hasSignature}
              style={{
                marginTop: '0.75rem',
                padding: '0.5rem 1rem',
                background: hasSignature ? '#fee' : '#f5f5f5',
                color: hasSignature ? '#e74c3c' : '#999',
                border: 'none',
                borderRadius: '6px',
                fontSize: '0.85rem',
                fontWeight: '600',
                cursor: hasSignature ? 'pointer' : 'not-allowed',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.5rem',
                alignSelf: 'flex-start'
              }}
            >
              <RotateCcw size={16} />
              Clear
            </button>
          </div>
        </div>

        {/* Form Section */}
        <div style={{
          flex: isLandscape ? 1 : 'none',
          padding: isLandscape ? '1.5rem' : '1rem',
          overflow: 'auto',
          display: 'flex',
          flexDirection: 'column',
          gap: '1rem'
        }}>
          {/* Printed Name */}
          <div>
            <label style={{
              display: 'block',
              fontSize: '0.8rem',
              fontWeight: '600',
              color: '#666',
              marginBottom: '0.5rem',
              textTransform: 'uppercase'
            }}>
              <span style={{ color: '#F05A28' }}>Step 2:</span> Print Your Name *
            </label>
            <input
              type="text"
              value={printedName}
              onChange={(e) => setPrintedName(e.target.value)}
              placeholder="Enter your full name"
              required
              style={{
                width: '100%',
                padding: '0.75rem',
                border: '2px solid #e0e0e0',
                borderRadius: '8px',
                fontSize: '1rem',
                outline: 'none',
                boxSizing: 'border-box'
              }}
              onFocus={(e) => e.currentTarget.style.borderColor = '#F05A28'}
              onBlur={(e) => e.currentTarget.style.borderColor = '#e0e0e0'}
            />
          </div>

          {/* Date & Time */}
          <div style={{
            background: '#f9f9f9',
            padding: '0.75rem',
            borderRadius: '8px',
            fontSize: '0.8rem',
            color: '#666'
          }}>
            <div><strong>Date:</strong> {new Date().toLocaleDateString()}</div>
            <div><strong>Time:</strong> {new Date().toLocaleTimeString()}</div>
          </div>

          {/* Legal Notice */}
          <div style={{
            background: '#fff9e6',
            border: '1px solid #f39c12',
            borderRadius: '8px',
            padding: '0.75rem',
            fontSize: '0.7rem',
            color: '#666',
            lineHeight: 1.5
          }}>
            <strong style={{ color: '#f39c12' }}>LEGAL:</strong> By signing, you agree this electronic signature is legally binding.
          </div>

          {/* Submit Button */}
          <button
            onClick={saveSignature}
            disabled={!hasSignature || !printedName.trim()}
            style={{
              padding: '1rem',
              background: (!hasSignature || !printedName.trim()) ? '#ccc' : '#4ecca3',
              color: (!hasSignature || !printedName.trim()) ? '#666' : '#1a1a1a',
              border: 'none',
              borderRadius: '8px',
              fontSize: '1.1rem',
              fontWeight: '700',
              cursor: (!hasSignature || !printedName.trim()) ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0.5rem',
              marginTop: 'auto'
            }}
          >
            <Check size={22} />
            Accept & Sign
          </button>
        </div>
      </div>
    </div>
  );
}
